import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { sendBilldeskResponse } from '../../../redux/CommandState/commands/helpers/sendBilldeskResponse';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { configSelector } from '../../../redux/ConfigState/ConfigState.selectors';
import React, { useEffect, useState, useCallback } from 'react';
import { colors } from '@chegg-ui/theme';
import { useDispatch } from 'react-redux';
import { StatusTypes, DropInPaymentsCommandNames, sendRioEvent, SpecType, SubType, ElementType } from '@chegg/commerce-utils';
import isMobileUserAgent from '../../../utils/isMobileUserAgent';
import hideGladlyAndCookieConcent from '../../../utils/hideGladlyAndCookieConcent';
import { doFireStatusUpdate } from '../../../utils/CallbackUtils';
import { ERRORS } from '../../../constants/Errors';
import { saveConfigs } from '../../../redux/ConfigState/ConfigState.actions';
import { sendRioEventCallback } from '../../../utils/CallbackUtils';
var BillDeskSDK = function BillDeskSDK() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    scriptsLoaded = _useState2[0],
    setScriptsLoaded = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    billdeskOpen = _useState4[0],
    setBilldeskOpen = _useState4[1];
  var dispatch = useDispatch();
  var _useAppSelector = useAppSelector(configSelector),
    upiParams = _useAppSelector.upiParams,
    appConstants = _useAppSelector.appConstants,
    rioNamespace = _useAppSelector.rioNamespace;
  useEffect(function () {
    var isMobile = isMobileUserAgent(window.navigator.userAgent);
    if (isMobile) hideGladlyAndCookieConcent(billdeskOpen);
  }, [billdeskOpen]);

  // response handler for billdesk end of flow
  var responseHandler = useCallback(function (_ref) {
    var status = _ref.status,
      txnResponse = _ref.txnResponse;
    var parsedTxnResponse = !!txnResponse && parseJwt(txnResponse.transaction_response);
    var parsedPaymentStatus = parsedTxnResponse && parsedTxnResponse.mandate.status;
    if (status === 111) {
      // billdesk modal was closed by the user or there was a timeout
      sendRioEvent({
        callback: sendRioEventCallback,
        info: 'Billdesk modal was closed by the user or there was a timeout',
        specType: SpecType.INTERACTION,
        subType: SubType.CLICK,
        elementText: 'close vendor modal',
        elementType: ElementType.ICON,
        elementValue: 'billdesk',
        namespace: rioNamespace
      });
      window.location.reload();
      return;
    }
    if (status.toString().startsWith('3') || parsedPaymentStatus === 'rejected' || txnResponse.status === 500) {
      dispatch(saveConfigs({
        ui: {
          showUpiProcessing: false
        }
      }));
      reportErrorToCheggWeb();
      return;
    }
    sendBilldeskResponse(status, txnResponse);
  }, [dispatch]);
  useEffect(function () {
    // Define script loading function
    var loadScript = function loadScript(src) {
      var module = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      return new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = src;
        script.async = true;
        if (module) {
          script.type = 'module';
        }
        document.head.appendChild(script);
        script.onerror = function () {
          responseHandler({
            status: 300
          });
        };
        script.onerror = reject;
        script.onload = resolve;
      });
    };

    // Define stylesheet loading function
    var loadStylesheet = function loadStylesheet(href) {
      var link = document.createElement('link');
      link.href = href;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    };
    if (appConstants) {
      var scriptsSubDomain = appConstants.billdesk.scriptsSubDomain;
      var loadBillDeskScripts = function loadBillDeskScripts(scriptsSubDomain) {
        var domain = "https://".concat(scriptsSubDomain, ".billdesk.com");
        var environmentPath = scriptsSubDomain === 'pay' ? '/jssdk/v1/dist' : '/merchant-uat/sdk/dist';
        loadScript("".concat(domain).concat(environmentPath, "/billdesksdk/billdesksdk.esm.js"), true);
        loadScript("".concat(domain).concat(environmentPath, "/billdesksdk.js"));
        loadStylesheet("".concat(domain).concat(environmentPath, "/billdesksdk/billdesksdk.css"));
      };
      Promise.all([loadScript('https://ajax.googleapis.com/ajax/libs/jquery/3.4.1/jquery.min.js'), loadScript('https://stackpath.bootstrapcdn.com/bootstrap/3.4.1/js/bootstrap.min.js'), loadBillDeskScripts(scriptsSubDomain)]).then(function () {
        setScriptsLoaded(true);
      });

      // Load stylesheets
      /* TODO: this stylesheet breaks the DCO design. Temporarily disabled
      loadStylesheet(
        'https://stackpath.bootstrapcdn.com/bootstrap/3.4.1/css/bootstrap.min.css'
      );
      */
      // workaround for test
      if (scriptsSubDomain === 'invalid-subdomain') {
        responseHandler({
          status: 300
        });
      }
    }
  }, [appConstants, responseHandler]);
  var paymentCategories = ['upi'];
  var loadXMLDoc = useCallback(function () {
    // Define your config here. I've used placeholders for the merchantId and merchantLogo
    var flowConfig = {
      merchantId: appConstants === null || appConstants === void 0 ? void 0 : appConstants.billdesk.merchantId,
      bdOrderId: upiParams === null || upiParams === void 0 ? void 0 : upiParams.orderId,
      authToken: upiParams === null || upiParams === void 0 ? void 0 : upiParams.authToken,
      childWindow: true,
      prefs: {
        payment_categories: paymentCategories
      },
      retryCount: 0
    };
    var themeConfig = {
      sdkPrimaryColor: colors.dark,
      sdkAccentColor: colors.dark,
      sdkBackgroundColor: colors.white,
      sdkBannerColor: colors.white
    };
    var config = {
      merchantLogo: 'data:image/png;base64,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',
      flowConfig: flowConfig,
      themeConfig: themeConfig,
      responseHandler: responseHandler,
      flowType: 'payments'
    };
    // @ts-ignore
    window.loadBillDeskSdk(config);
    setBilldeskOpen(true);
  }, [upiParams, appConstants, responseHandler]);
  useEffect(function () {
    if (!billdeskOpen && scriptsLoaded && upiParams !== null && upiParams !== void 0 && upiParams.orderId && upiParams.authToken && appConstants) {
      loadXMLDoc();
    }
  }, [upiParams, loadXMLDoc, scriptsLoaded, appConstants, billdeskOpen]);
  return /*#__PURE__*/React.createElement("div", {
    "data-test": 'bill-desk-sdk'
  });
};
export var reportErrorToCheggWeb = function reportErrorToCheggWeb() {
  doFireStatusUpdate([{
    status: StatusTypes.ERROR,
    command: DropInPaymentsCommandNames.BILLDESK_RESPONSE_HANDLER,
    payload: {
      errors: [ERRORS.UPI_ERROR]
    }
  }]);
};
export var parseJwt = function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};
export var TestableBillDeskSDK = {
  BillDeskSDK: BillDeskSDK,
  reportErrorToCheggWeb: reportErrorToCheggWeb
};
export default BillDeskSDK;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/payment-methods/dist/esm/business/processors/billdesk/Billdesk.js
// module id = 4595
// module chunks = 11